import { Color } from './java/Color';
import { Settings } from './Settings';

export class GamePalette {

  // Difficulty themes
  // Normal: clean teal progression
  private static readonly DIFF_ACCENTS: Color[][] = [
    [ // Normal — teal to blue to gold
      new Color(78, 205, 196), new Color(100, 170, 240), new Color(140, 130, 255),
      new Color(180, 140, 220), new Color(200, 160, 100), new Color(255, 200, 80),
    ],
    [ // Hard — warm amber/orange tones
      new Color(245, 180, 60), new Color(240, 150, 50), new Color(235, 120, 60),
      new Color(230, 100, 80), new Color(220, 80, 60), new Color(255, 130, 40),
    ],
    [ // Insane — deep red/purple aggressive
      new Color(220, 50, 80), new Color(200, 50, 140), new Color(160, 50, 200),
      new Color(140, 40, 220), new Color(180, 30, 100), new Color(255, 50, 50),
    ]
  ];

  private static readonly DIFF_BG_TINTS: Color[][] = [
    [ // Normal
      new Color(12, 25, 28), new Color(12, 18, 32), new Color(18, 14, 30),
      new Color(22, 14, 25), new Color(25, 18, 12), new Color(28, 22, 10),
    ],
    [ // Hard
      new Color(28, 20, 10), new Color(30, 18, 10), new Color(30, 15, 10),
      new Color(30, 12, 10), new Color(28, 10, 8), new Color(30, 16, 6),
    ],
    [ // Insane
      new Color(28, 8, 14), new Color(25, 8, 20), new Color(20, 8, 28),
      new Color(18, 6, 30), new Color(24, 6, 14), new Color(30, 8, 8),
    ]
  ];

  private static difficulty: number = 0;
  private static currentR: number = 0;
  private static currentG: number = 0;
  private static currentB: number = 0;
  private static targetR: number = 0;
  private static targetG: number = 0;
  private static targetB: number = 0;
  private static bgR: number = 0;
  private static bgG: number = 0;
  private static bgB: number = 0;
  private static bgTargetR: number = 0;
  private static bgTargetG: number = 0;
  private static bgTargetB: number = 0;
  private static initialized: boolean = false;

  private static readonly LERP_SPEED: number = 0.02;

  static setDifficulty(diff: number): void {
    GamePalette.difficulty = Math.max(0, Math.min(2, diff));
  }

  static getDifficulty(): number {
    return GamePalette.difficulty;
  }

  /** Background animation speed multiplier */
  static getGeoSpeed(): number {
    return GamePalette.difficulty === 0 ? 1 : GamePalette.difficulty === 1 ? 1.6 : 2.4;
  }

  /** Particle/visual density multiplier */
  static getParticleDensity(): number {
    return GamePalette.difficulty === 0 ? 1 : GamePalette.difficulty === 1 ? 1.3 : 1.8;
  }

  /** Screen distortion intensity (Insane only) */
  static getDistortion(): number {
    return GamePalette.difficulty === 2 ? 1 : 0;
  }

  static update(level: number): void {
    const idx = GamePalette.getIndex(level);
    const accents: Color[] = GamePalette.DIFF_ACCENTS[GamePalette.difficulty];
    const bgTints: Color[] = GamePalette.DIFF_BG_TINTS[GamePalette.difficulty];
    const target: Color = accents[idx];
    const bgTarget: Color = bgTints[idx];

    GamePalette.targetR = target.getRed();
    GamePalette.targetG = target.getGreen();
    GamePalette.targetB = target.getBlue();
    GamePalette.bgTargetR = bgTarget.getRed();
    GamePalette.bgTargetG = bgTarget.getGreen();
    GamePalette.bgTargetB = bgTarget.getBlue();

    if (!GamePalette.initialized) {
      GamePalette.currentR = GamePalette.targetR;
      GamePalette.currentG = GamePalette.targetG;
      GamePalette.currentB = GamePalette.targetB;
      GamePalette.bgR = GamePalette.bgTargetR;
      GamePalette.bgG = GamePalette.bgTargetG;
      GamePalette.bgB = GamePalette.bgTargetB;
      GamePalette.initialized = true;
    }

    GamePalette.currentR += (GamePalette.targetR - GamePalette.currentR) * GamePalette.LERP_SPEED;
    GamePalette.currentG += (GamePalette.targetG - GamePalette.currentG) * GamePalette.LERP_SPEED;
    GamePalette.currentB += (GamePalette.targetB - GamePalette.currentB) * GamePalette.LERP_SPEED;
    GamePalette.bgR += (GamePalette.bgTargetR - GamePalette.bgR) * GamePalette.LERP_SPEED;
    GamePalette.bgG += (GamePalette.bgTargetG - GamePalette.bgG) * GamePalette.LERP_SPEED;
    GamePalette.bgB += (GamePalette.bgTargetB - GamePalette.bgB) * GamePalette.LERP_SPEED;
  }

  // Cached accent — recomputed only when the underlying RGB or colorblind mode changes.
  // accent() is called many times per frame but currentR/G/B only lerp once per tick, so
  // this returns the same immutable Color instead of allocating (+ HSB math) on every call.
  private static cachedAccent: Color | null = null;
  private static cachedR: number = -1;
  private static cachedG: number = -1;
  private static cachedB: number = -1;
  private static cachedColorblind: boolean = false;

  static accent(alpha?: number): Color {
    if (alpha !== undefined) {
      const base = GamePalette.accent();
      return new Color(base.getRed(), base.getGreen(), base.getBlue(), Math.max(0, Math.min(255, alpha)));
    }
    const r = GamePalette.clamp(GamePalette.currentR);
    const g = GamePalette.clamp(GamePalette.currentG);
    const b = GamePalette.clamp(GamePalette.currentB);
    const cb = Settings.getColorblindMode();
    if (GamePalette.cachedAccent === null || r !== GamePalette.cachedR || g !== GamePalette.cachedG
        || b !== GamePalette.cachedB || cb !== GamePalette.cachedColorblind) {
      const base = new Color(r, g, b);
      GamePalette.cachedAccent = cb ? GamePalette.toColorblind(base) : base;
      GamePalette.cachedR = r;
      GamePalette.cachedG = g;
      GamePalette.cachedB = b;
      GamePalette.cachedColorblind = cb;
    }
    return GamePalette.cachedAccent;
  }

  /** Remap a color toward a deuteranopia-friendly palette (blue/orange/yellow) */
  private static toColorblind(c: Color): Color {
    const hsb = Color.RGBtoHSB(c.getRed(), c.getGreen(), c.getBlue());
    let hue = hsb[0];
    // Shift greens/reds to blue/orange range
    // Red (0.0/1.0) → orange (0.08), green (0.33) → blue (0.58), teal (0.5) → blue (0.6)
    if (hue < 0.15 || hue > 0.9) {
      hue = 0.08; // reds → orange
    } else if (hue < 0.45) {
      hue = 0.58; // greens/yellows → blue
    } else if (hue < 0.7) {
      hue = 0.62; // teals/cyans → deeper blue
    } else {
      hue = 0.75; // purples stay in blue-violet range
    }
    return Color.getHSBColor(hue, Math.min(hsb[1] * 1.1, 1), hsb[2]);
  }

  static bgTint(alpha?: number): Color {
    if (alpha !== undefined) {
      return new Color(GamePalette.clamp(GamePalette.bgR), GamePalette.clamp(GamePalette.bgG),
        GamePalette.clamp(GamePalette.bgB), Math.max(0, Math.min(255, alpha)));
    }
    return new Color(GamePalette.clamp(GamePalette.bgR), GamePalette.clamp(GamePalette.bgG),
      GamePalette.clamp(GamePalette.bgB));
  }

  static reset(): void {
    GamePalette.initialized = false;
  }

  private static getIndex(level: number): number {
    if (level <= 4) return 0;
    if (level <= 7) return 1;
    if (level <= 10) return 2;
    if (level <= 14) return 3;
    if (level <= 19) return 4;
    return 5;
  }

  private static clamp(v: number): number {
    return Math.max(0, Math.min(255, Math.round(v)));
  }
}
